import { useCallback, useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { CookieConsentPreferences, CookieConsentRecord } from '../types';

const CONSENT_COOKIE_NAME = 'eu_cookie_consent';
const CONSENT_VERSION = '1.0';

interface UseCookieConsentReturn {
  isVisible: boolean;
  preferences: CookieConsentPreferences;
  consentRecord: CookieConsentRecord | null;
  saveConsent: (preferences: CookieConsentPreferences) => void;
  acceptAll: (categories: string[]) => void;
  rejectAll: (requiredCategories: string[]) => void;
  hideConsentBanner: () => void;
  showSettings: () => void;
  hasConsented: boolean;
  needsConsent: boolean;
}

export const useCookieConsent = (
  categories: Array<{ id: string; isRequired: boolean }>,
  consentDurationDays: number = 365
): UseCookieConsentReturn => {
  const [cookies, setCookie] = useCookies([CONSENT_COOKIE_NAME]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<CookieConsentPreferences>({});
  const [consentRecord, setConsentRecord] = useState<CookieConsentRecord | null>(null);

  // Initialize consent state
  useEffect(() => {
    const existingConsent = cookies[CONSENT_COOKIE_NAME];
    
    if (existingConsent && typeof existingConsent === 'object') {
      // Check if consent is still valid
      const isExpired = Date.now() - existingConsent.timestamp > (consentDurationDays * 24 * 60 * 60 * 1000);
      const isOldVersion = existingConsent.version !== CONSENT_VERSION;
      
      if (!isExpired && !isOldVersion) {
        setConsentRecord(existingConsent);
        setPreferences(existingConsent.preferences);
        setIsVisible(false);
        return;
      }
    }
    
    // No valid consent found, show banner
    setIsVisible(true);
    
    // Initialize with default preferences (required cookies only)
    const defaultPreferences: CookieConsentPreferences = {};
    categories.forEach(category => {
      defaultPreferences[category.id] = category.isRequired;
    });
    setPreferences(defaultPreferences);
  }, [categories, consentDurationDays, cookies]);

  const saveConsent = useCallback((newPreferences: CookieConsentPreferences) => {
    const timestamp = Date.now();
    const consentString = Object.entries(newPreferences)
      .map(([key, value]) => `${key}:${value ? '1' : '0'}`)
      .join(',');

    const record: CookieConsentRecord = {
      preferences: newPreferences,
      timestamp,
      version: CONSENT_VERSION,
      consentString
    };

    // Set cookie with secure options
    setCookie(CONSENT_COOKIE_NAME, record, {
      path: '/',
      maxAge: consentDurationDays * 24 * 60 * 60, // Convert days to seconds
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax'
    });

    setConsentRecord(record);
    setPreferences(newPreferences);
    setIsVisible(false);

    // Fire consent event for GTM
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent: record
      });
    }

    // Update GTM consent
    updateGTMConsent(newPreferences);
  }, [setCookie, consentDurationDays]);

  const acceptAll = useCallback((categoryIds: string[]) => {
    const allAccepted: CookieConsentPreferences = {};
    categoryIds.forEach(id => {
      allAccepted[id] = true;
    });
    saveConsent(allAccepted);
  }, [saveConsent]);

  const rejectAll = useCallback((requiredCategoryIds: string[]) => {
    const onlyRequired: CookieConsentPreferences = {};
    categories.forEach(category => {
      onlyRequired[category.id] = requiredCategoryIds.includes(category.id);
    });
    saveConsent(onlyRequired);
  }, [saveConsent, categories]);

  const hideConsentBanner = useCallback(() => {
    setIsVisible(false);
  }, []);

  const showSettings = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hasConsented = Boolean(consentRecord);
  const needsConsent = isVisible && !hasConsented;

  return {
    isVisible,
    preferences,
    consentRecord,
    saveConsent,
    acceptAll,
    rejectAll,
    hideConsentBanner,
    showSettings,
    hasConsented,
    needsConsent
  };
};

// Update Google Tag Manager consent state
const updateGTMConsent = (preferences: CookieConsentPreferences) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      ad_storage: preferences.marketing ? 'granted' : 'denied',
      functionality_storage: preferences.preferences ? 'granted' : 'denied',
      personalization_storage: preferences.preferences ? 'granted' : 'denied',
      security_storage: 'granted' // Always granted for necessary cookies
    });
  }
};
